import * as React from 'react'

import {
  IconButton,
  IconSearch,
  InputGroup,
  Pagination,
  Space,
  Text,
  TextField,
} from '@procurify/ui'
import { type MessageDescriptor } from 'react-intl'
import { useTheme } from 'styled-components'

import { useProcurifyIntl } from '@webapp/hooks'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { ListHeaderStyled } from './styles/ListHeaderStyled'
import { ListRowStyled } from './styles/ListRowStyled'
import { PaginationFooterStyled } from './styles/PaginationFooterStyled'

export interface CatalogPermissionConfigListMessages {
  listHeader: MessageDescriptor
  searchPlaceholder: MessageDescriptor
  searchAriaLabel: MessageDescriptor
  searchButtonAriaLabel: MessageDescriptor
  emptyState: MessageDescriptor
  errorState: MessageDescriptor
}

export interface ICatalogPermissionConfigListProps<T extends { id: number }> {
  items: T[]
  getItemLabel: (item: T) => string
  selectedId: Nullable<number>
  onSelect: (item: T) => void
  isLoading: boolean
  isError: boolean
  searchValue: string
  onSearchChange: (value: string) => void
  onSearchSubmit: (value: string) => void
  paging: RepoPaginationResponse
  onPageChange: (page: number) => void
  messages: CatalogPermissionConfigListMessages
}

/**
 * Shared master-list for the catalog-permission config tabs (users /
 * secondary-categories): searchable, paginated, single-select. Feature views
 * supply the item type, row-label getter, and their own i18n messages.
 */
export const CatalogPermissionConfigList = <T extends { id: number }>({
  items,
  getItemLabel,
  selectedId,
  onSelect,
  isLoading,
  isError,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  paging,
  onPageChange,
  messages,
}: ICatalogPermissionConfigListProps<T>) => {
  const intl = useProcurifyIntl()
  const theme = useTheme()

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchSubmit(searchValue)
    }
  }

  return (
    <Space>
      <InputGroup>
        <TextField
          fullWidth
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          aria-label={intl.formatMessage(messages.searchAriaLabel)}
          disabled={isLoading}
        />
        <InputGroup.Item>
          <IconButton
            iconComponent={<IconSearch />}
            aria-label={intl.formatMessage(messages.searchButtonAriaLabel)}
            onClick={() => onSearchSubmit(searchValue)}
          />
        </InputGroup.Item>
      </InputGroup>

      <div>
        <ListHeaderStyled>
          <Text bold color={theme.colors.text.tertiary}>
            {intl.formatMessage(messages.listHeader)}
          </Text>
        </ListHeaderStyled>

        {isError && <Text>{intl.formatMessage(messages.errorState)}</Text>}

        {!isError && !isLoading && items.length === 0 && (
          <Text>{intl.formatMessage(messages.emptyState)}</Text>
        )}

        {!isError &&
          items.map((item) => (
            <ListRowStyled
              key={item.id}
              type='button'
              $selected={item.id === selectedId}
              onClick={() => onSelect(item)}
            >
              {getItemLabel(item)}
            </ListRowStyled>
          ))}
      </div>

      <PaginationFooterStyled>
        <Space direction='horizontal' justify='end'>
          <Pagination
            currentPage={paging.page}
            numPages={paging.numPages}
            onPageChange={onPageChange}
          />
        </Space>
      </PaginationFooterStyled>
    </Space>
  )
}
